import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from './auth-context';
import { signOut } from './auth';
import {
  DancerSessionError,
  getDancerSessionStatus,
  loginDancerSession,
  logoutDancerSession,
  removeDancerPushDevice,
} from './dancerSession';
import { runDancerLogout } from './dancerSessionLogout';
import { releaseDancerPushDeviceOnSignOut } from './dancerPushDevice';
import { clearPushPreviewIdentity } from './pushPreview';
import { isJrStaffDancerPrincipal, readJrStaffDancerClaim, signInAsJrStaffDancer } from './jrStaffDancerSession';
import { jrStaffPromotionState, type JrStaffPromotionState } from './jrStaffPromotionState';
import {
  clearNoDancerHint,
  hasNoDancerHint,
  markNoDancerHint,
  statusCheckFailureState,
} from './dancerSessionHint';
import { clearLastHomeShell, forgetHomeShellKind, readLastHomeShell } from './lastHomeShell';
import {
  forgetDancerSignInEnded,
  hasDancerSignInEndedNote,
  rememberDancerSignInEnded,
} from './dancerSignInEnded';
import {
  cookieNotKeptNotice,
  dancerCookieWasNotKept,
  isHomeScreenApp,
  rememberJustSignedIn,
  type JustSignedIn,
} from './dancerCookieNotKept';
import { clearSentryDancerSession } from './sentry';
import { forgetDancerCostumeStates } from './dancerCostumeStates';

type DancerSessionState = 'loading' | 'signed-out' | 'signed-in' | 'error';

/* The three-state answer, and the decision itself, live in
   lib/jrStaffPromotionState.ts so a test can walk a whole login sequence in
   node. Re-exported here because this context is where every consumer already
   reads the session from. */
export type { JrStaffPromotionState };

interface DancerSessionContextValue {
  state: DancerSessionState;
  authenticated: boolean;
  /**
   * Packet R38 Fix 1: true only when THIS device's last real check, login,
   * or logout positively confirmed no dancer cookie is present. HubContent
   * (App.tsx) reads this to decide whether an already-signed-in adult can
   * render immediately while `state` is still 'loading', instead of
   * waiting on the same uncacheable fetch every time. See
   * lib/dancerSessionHint.ts for the full safety reasoning; this value is
   * captured once at mount (a device with no hint yet always starts false,
   * the safe default), which is all HubContent ever needs it for.
   */
  noDancerHint: boolean;
  /**
   * This dancer is a PROMOTED JR STAFF: designated by a Director, and
   * signed in to a Firebase principal as well as their dancer cookie. Two
   * sessions, one person (see lib/jrStaffDancerSession.ts).
   *
   * Consumers use it to answer "is this a child-only session?" - which is
   * what `authenticated` alone used to mean and no longer does. A promoted
   * Jr Staff is staff, so the route clamp and the suppressed adult chrome
   * must NOT apply to them, or the parity ruling dies on a redirect.
   */
  jrStaffDancer: boolean;
  /**
   * The same fact with its middle state kept, for anything that would
   * otherwise say "not promoted" out loud while the exchange is still in
   * flight. Never use this to open a door: `jrStaffDancer` is the gate.
   */
  jrStaffPromotion: JrStaffPromotionState;
  csrfToken: string | null;
  error: string | null;
  sessionNotice: string | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  retry: () => void;
  updateCsrfToken: (csrfToken: string) => void;
}

const DancerSessionContext = createContext<DancerSessionContextValue | null>(null);

// Neutral on purpose: the same answer follows her year running out, a
// password reset or a login a grown-up turned off, so it must never promise
// the old password works.
const DANCER_SIGN_IN_ENDED_NOTICE =
  'Your dancer sign-in ended. Sign in again with your dancer username and password. If that does not work, ask a grown-up to check your dancer login.';

// The sibling of the notice above is lib/dancerCookieNotKept.ts
// cookieNotKeptNotice(): a different fact. The login answered 200 and the very
// next status check found no cookie and gave no reason, so the browser never
// kept the cookie (suspected cause: the iOS home-screen web app, WebKit bug
// 272325; not proven). The password was right, so it must say so and must
// never read like a wrong password or a reset; the ended notice above would
// be false here, because nothing ended.

// How long login() waits before asking the confirming status question a second
// time after a network failure. Short: it is a blip, not an outage.
const CONFIRM_RETRY_DELAY_MS = 750;

// B15 (2026-10-25, "dancers lose their sign-in on the home-screen app"). A
// status answer with no cookie and no reason, on a device that holds a dancer
// sign-in, is asked again after each of these waits before it is believed.
// The iPhone home-screen app has been reported (WebKit bug 272325 and the
// reports under it) to send an open's FIRST requests without the cookies it
// is holding, and to send them again a moment later. One such answer used to
// be final: the sign-in card, "Your dancer sign-in ended", her remembered
// home forgotten, while the cookie sat in the jar the whole time. A server
// refusal (reason 'session_ended') is never re-asked, and a device with no
// dancer sign-in to lose is never re-asked, so a first visit is not slowed.
// About two seconds at most, and only on the path that was going to sign her
// out anyway.
const COOKIELESS_REASK_DELAYS_MS = [700, 1500] as const;

// The account check's own words when the Jr Staff claim cannot be read while
// her cookie is live: a failed check with Try again, never a sign-out.
const CLAIM_UNREADABLE_MESSAGE =
  'The account check could not finish. Check your connection and try again.';

function messageFor(error: unknown): string {
  if (error instanceof DancerSessionError) return error.message;
  return 'The dancer login could not connect. Check your connection and try again.';
}

export function DancerSessionProvider({ children }: { children: ReactNode }) {
  const { user: firebaseUser, loading: firebaseLoading } = useAuth();
  const [state, setState] = useState<DancerSessionState>('loading');
  const [csrfToken, setCsrfToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sessionNotice, setSessionNotice] = useState<string | null>(null);
  // That a dancer login JUST succeeded in this tab, and when. In memory only,
  // on purpose: a reload loses it, and that is correct, because the evidence
  // is only good for the next status answer in this same page. Nothing she
  // typed is kept. Taken ONCE by the status effect below, before any branch,
  // and dropped by a portal 200 (updateCsrfToken); see lib/dancerCookieNotKept.ts.
  const justSignedInRef = useRef<JustSignedIn | null>(null);
  // Bumped by every login attempt and every sign-out. The confirming status
  // call in login() was SENT before a sign-out can happen, so its answer can
  // still say "authenticated" after she has signed herself out; it exchanges
  // the Jr Staff token only if no later login or sign-out has happened since.
  // Otherwise a principal would be minted for a session she just ended, the
  // next check would find no cookie, and she would read "Your dancer sign-in
  // ended" after signing herself out.
  const loginGenerationRef = useRef(0);
  const [retryKey, setRetryKey] = useState(0);
  // Every way a dancer sign-in ends lands here (logout, a refused cookie, a
  // failed status check), and none of them reloads the page. The Costumes
  // tab's shared refresh (lib/dancerCostumeStates.ts) keeps her last answer
  // in module memory, so it is dropped the moment there is no sign-in: the
  // next dancer on this tab, a sister with the same costume cards, must
  // never see it. The module also keys by token, so this is the belt to
  // that braces.
  useEffect(() => {
    if (!csrfToken) forgetDancerCostumeStates();
  }, [csrfToken]);
  // Usually the server says so itself: the cookie outlives her one-year
  // sign-in by a week (DECISIONS 48), so a phone opened in that week sends a
  // cookie the server refuses with reason 'session_ended'. Opened later than
  // that, the browser has already dropped the cookie and the server cannot
  // tell her from a first visit. A remembered dancer shell is safe evidence
  // that THIS device used to hold a dancer session. Capture only the kind,
  // before the signed-out path clears the shell and its first name for
  // shared-device privacy. tests/harness/dancer-session-ended proves both
  // paths by what renders.
  const [hadDancerShell] = useState(() => readLastHomeShell()?.kind === 'dancer');
  // B15: does THIS device hold a dancer sign-in right now, as far as this page
  // knows? Starts from the remembered dancer home, turns true on a live answer
  // or a login the server accepted, and false on a final "no cookie" answer or
  // a deliberate sign-out. Only a device where this is true re-asks a
  // cookie-less status answer (COOKIELESS_REASK_DELAYS_MS).
  const cookieExpectedRef = useRef(hadDancerShell);
  // The third piece of evidence, for a PROMOTED Jr Staff dancer: her refused
  // cookie sends her through the full sign-out, which reloads the page and
  // takes the cookie, the remembered home and this provider's state with it.
  // The dead-cookie branch below leaves a one-tab note before that sign-out
  // starts (lib/dancerSignInEnded.ts); this reads it once at mount. A ref,
  // not state: the first answer that reaches the ordinary path below takes
  // it, so a later retry in the same page never repeats it, and it is not
  // something the status effect should re-run on.
  const endedNoteRef = useRef<boolean | null>(null);
  if (endedNoteRef.current === null) endedNoteRef.current = hasDancerSignInEndedNote();
  // Captured once, at mount: reading localStorage is cheap, but the value
  // only matters while `state` is still 'loading', and it is kept current
  // for the NEXT load by the writes below, not by re-reading mid-session.
  const [noDancerHint] = useState(() => hasNoDancerHint());
  // Resolved from the ID TOKEN, never from a Firestore document: custom
  // claims cannot be written by a client, so this carve-out has no path in
  // from the browser. NULL means not answered yet and reads as false at every
  // gate, so an unreadable token still leaves a promoted dancer on the
  // ordinary child-only surface rather than opening staff routes on a guess;
  // the null is only ever the difference between "no" and "not yet" for the
  // screens that say it out loud.
  const [jrStaffDancer, setJrStaffDancer] = useState<boolean | null>(null);
  // TRUE while a minted token is being redeemed. During that window there is
  // no Firebase principal to read a claim off, so the claim effect below
  // answers false quite correctly and the answer is still not the final one.
  const [promotionPending, setPromotionPending] = useState(false);

  useEffect(() => {
    // Auth itself has not settled: nothing is known, so say nothing is known.
    if (firebaseLoading) {
      setJrStaffDancer(null);
      return;
    }
    // No principal at all. That is a real, settled answer unless a token is
    // still being redeemed, and promotionPending is what carries that.
    if (!firebaseUser) {
      setJrStaffDancer(false);
      return;
    }
    // A principal arrived, so whatever exchange was in flight has landed.
    setPromotionPending(false);
    // ...and its claim has not been read yet, so a leftover false from the
    // no-principal branch above is stale: say "not yet" (null) rather than
    // "no", or a screen reads the gap as a plain dancer for a moment. Only a
    // false is reset; a true stays true, so a re-run for the same signed-in
    // Jr Staff can never flip her to child-only routes mid-session. A null
    // already reads as false at every gate, so no route moves with this.
    setJrStaffDancer((previous) => (previous === true ? previous : null));
    let current = true;
    void isJrStaffDancerPrincipal(firebaseUser).then((value) => {
      if (current) setJrStaffDancer(value);
    });
    return () => {
      current = false;
    };
    // retryKey too (B15): when the claim could not be read, Try again re-runs
    // the status effect, and this answer must be read again with it, or she
    // comes back signed in with her staff surfaces still shut for this load.
  }, [firebaseLoading, firebaseUser, retryKey]);

  useEffect(() => {
    if (firebaseLoading) return;
    let current = true;
    setState('loading');
    setError(null);
    setSessionNotice(null);

    void (async () => {
      try {
        let status = await getDancerSessionStatus();
        if (!current) return;
        // B15: a cookie-less answer on a device that holds a dancer sign-in is
        // asked again before it is believed (see COOKIELESS_REASK_DELAYS_MS).
        // A re-ask that fails on the network keeps the answer already in hand:
        // it is not new evidence, and it must not turn a sign-in card into the
        // error screen.
        for (const delay of COOKIELESS_REASK_DELAYS_MS) {
          if (status.authenticated || status.reason !== null) break;
          // A login the server accepted set this too, so the answer right
          // after a login is re-asked as well.
          if (!cookieExpectedRef.current) break;
          await new Promise((resolve) => setTimeout(resolve, delay));
          if (!current) return;
          const again = await getDancerSessionStatus().catch(() => null);
          if (!current) return;
          if (!again) break;
          status = again;
        }
        cookieExpectedRef.current = status.authenticated;
        // A real answer arrived, so the note a Jr Staff sign-out left for this
        // load has been read (endedNoteRef, at mount) and is done. Forgotten
        // here, before any branch, so it cannot outlive this answer; the
        // dead-cookie branch below writes a fresh one when it applies.
        forgetDancerSignInEnded();
        // The "she just signed in" memory is taken HERE, once, before any
        // branch, and dropped. Every branch below that returns early (the
        // adult one, the promoted dead-cookie one) would otherwise leave it
        // behind to be read by a LATER, unrelated answer: an adult who signs
        // in and out in another tab, a wrong password typed in a second tab
        // (the server clears the cookie on any failed login), a sign-out in a
        // second tab. Any real answer settles the question for good.
        const justSignedIn = justSignedInRef.current;
        justSignedInRef.current = null;

        // A Firebase account is an adult account. It always wins, and any
        // leftover dancer cookie is removed before adult routes can mount.
        //
        // ONE EXCEPTION, and only one: a PROMOTED JR STAFF DANCER (parity
        // ruling, see lib/jrStaffDancerSession.ts). That Firebase principal
        // is not an adult who happens to be signed in; it is this same
        // dancer, minted by the dancer endpoint for the uid this very
        // cookie authenticates. Clearing the cookie under it would take
        // away their dancer portal the moment a Director made them Jr
        // Staff, which is the opposite of parity: they are meant to gain a
        // surface, never trade one away. The claim is read from the ID
        // token, which no client can write.
        //
        // B15: the claim is read ONCE, with "could not tell" (null) kept apart
        // from "no". A saved ID token past its hour needs the network to
        // refresh, and a home-screen app opened after a while often has none
        // yet. An unreadable claim used to read as "adult", and this branch
        // then logged the dancer cookie out ON THE SERVER: a promoted Jr Staff
        // lost her sign-in to a network blip. Now it decides nothing it cannot
        // undo. With a live cookie it is a failed check (Try again re-runs
        // this effect). With a refused cookie (session_ended) it takes the
        // full sign-out below, Firebase principal included, because a grown-up
        // turning her sign-in off must still end it. With no cookie and no
        // reason it is also a failed check: there is nothing to clear, and the
        // principal is not provably an adult's.
        const claim = firebaseUser ? await readJrStaffDancerClaim(firebaseUser) : false;
        if (!current) return;
        if (firebaseUser && claim === null && status.reason !== 'session_ended') {
          throw new DancerSessionError(CLAIM_UNREADABLE_MESSAGE, 0, 'jr_staff_claim_unreadable');
        }
        if (firebaseUser && claim === false) {
          if (status.authenticated && status.csrfToken) {
            await logoutDancerSession(status.csrfToken);
            if (!current) return;
          }
          setCsrfToken(null);
          setState('signed-out');
          // Real, resolved answer either way: this adult's device now
          // definitely holds no dancer cookie (either it never did, or the
          // stale one was just cleared above). Safe to mark for next load.
          // PACKET J12 ITEM 3: a dancer cookie was just evicted from under
          // an adult account, so any remembered DANCER home shell on this
          // device belongs to the kid who is no longer signed in here.
          //
          // Kind scoped, and it has to be (Codex review of PR #1099, P1).
          // This branch runs on EVERY adult boot, not only when a stale
          // cookie was really there, so the unconditional clear it used to
          // make wiped the adult's own staff or family shell on every start
          // and only survived because their own resolved boot happened to
          // rewrite it a moment later. See forgetHomeShellKind.
          //
          // ABOVE markNoDancerHint(), not below it: the S2-1 pin in
          // tests/dancer-session-client.test.mjs slices this branch on the
          // exact text "markNoDancerHint();\n return;\n }" and anything
          // wedged between those two lines truncates the slice.
          cookieExpectedRef.current = false;
          forgetHomeShellKind('dancer');
          markNoDancerHint();
          return;
        }
        if (!current) return;

        // A DEAD COOKIE MUST NOT LEAVE A LIVE FIREBASE PRINCIPAL (audit
        // S2-1). By this point firebaseUser, if present, is the promoted Jr
        // Staff principal - the adult branch above returned otherwise - or a
        // principal whose claim could not be read while the server refused a
        // dancer cookie (B15), which is torn down the same way rather than
        // left holding staff access. A parent's password reset or switching the
        // login off bumps credentialVersion, which kills the cookie
        // (status.authenticated goes false) but never touches the Firebase
        // refresh token: the manageLogin handler now revokes it server
        // side, and this is the client half, so a lost or shared phone does
        // not keep staff-level Firestore access after the cookie is gone.
        // There is no live cookie to clear here (that is exactly what
        // status.authenticated false means), so csrfToken is null and only
        // the Firebase teardown in runDancerLogout runs.
        if (firebaseUser && !status.authenticated) {
          // Her dancer sign-in ended (this principal only ever exists because
          // of one), and she must be told so on the sign-in card exactly as a
          // child-only dancer is (DECISIONS 48 final review, P3). The real
          // signOut ends in a reload, so the note has to be written BEFORE
          // it; the notice set below covers a teardown that returns instead.
          rememberDancerSignInEnded();
          await runDancerLogout({
            csrfToken: null,
            hasFirebaseSession: true,
            clearDancerCookie: logoutDancerSession,
            signOutFirebase: signOut,
          });
          if (!current) return;
          setCsrfToken(null);
          setState('signed-out');
          setSessionNotice(DANCER_SIGN_IN_ENDED_NOTICE);
          // Codex review of PR #1099, P1. This is a FULL sign-out: the
          // cookie is dead and runDancerLogout above tore the Firebase
          // principal down with it, so nothing about the last home on this
          // device is still true. This person's shell is a dancer shell
          // (their home is DancerHome even as Jr Staff), but the clear is
          // unconditional here for the same reason the logout callback's
          // is: there is no session of any kind left to remember one for.
          clearLastHomeShell();
          return;
        }

        // The staff half of the parity, started here because this is the one
        // place that already knows the dancer is signed in and whether the
        // server minted them a token. Deliberately fire-and-forget: the
        // dancer's own portal does not wait on Firebase, so a failed or slow
        // exchange never delays or blocks their sign-in. onAuthStateChanged
        // re-runs this effect once the session lands.
        if (status.authenticated && status.jrStaffToken && !firebaseUser) {
          setPromotionPending(true);
          void signInAsJrStaffDancer(status.jrStaffToken).then((started) => {
            // A FAILED exchange has to resolve, or the tile spins forever on
            // a session that is genuinely never going to be promoted this
            // load. Success needs no branch: the principal lands, the claim
            // effect above clears this on its way past.
            if (!started) setPromotionPending(false);
          });
        }

        setCsrfToken(status.authenticated ? status.csrfToken : null);
        setState(status.authenticated ? 'signed-in' : 'signed-out');
        // Three kinds of evidence that a dancer sign-in ended on this device:
        // the server refused her cookie, the phone remembers her dancer home,
        // or a promoted Jr Staff sign-out left its note for this load. The
        // note is taken once, so a retry later in this page never repeats it.
        const endedInThisTab = endedNoteRef.current === true;
        endedNoteRef.current = false;
        // A fourth kind, and the only one that is about a LOGIN rather than a
        // sign-in that used to exist: she signed in a moment ago in this tab
        // and the server sees no cookie and names no reason. A live cookie
        // proves it was kept, and a server reason is the other notice's to
        // speak for. The memory itself was taken above, before any branch.
        const cookieNotKept = dancerCookieWasNotKept(status, justSignedIn, Date.now());
        setSessionNotice(
          cookieNotKept
            ? cookieNotKeptNotice(isHomeScreenApp(typeof navigator === 'undefined' ? null : navigator))
            : !status.authenticated && (status.reason === 'session_ended' || hadDancerShell || endedInThisTab)
              ? DANCER_SIGN_IN_ENDED_NOTICE
              : null
        );
        // Same real-answer bookkeeping as the eviction branch above: a
        // confirmed dancer cookie clears any stale "no dancer" hint (a
        // future adult session on this device must not skip the wait), and
        // a confirmed absence marks it for a faster next load.
        if (status.authenticated) clearNoDancerHint();
        else markNoDancerHint();
        // THE CHILD-ONLY EXPIRY PATH (Codex review of PR #1099, P1). A
        // dancer cookie that simply expired, or was invalidated by a
        // parent's password change, has no adult account behind it, so it
        // never reaches the eviction branch above and lands here instead.
        // Without this the kid's first name stayed on the device and was
        // painted on every later cold open of the signed-out app, on a
        // phone that is very often shared.
        //
        // Written as its own `if` rather than folded into the two lines
        // above, because the exact one-line shape of those two is pinned by
        // tests/dancer-session-client.test.mjs (the no-dancer-hint test),
        // and turning them into a block breaks that pin.
        if (!status.authenticated) forgetHomeShellKind('dancer');
      } catch (caught) {
        if (!current) return;
        setCsrfToken(null);
        // Fable review, PR #901, Medium 1: a device that already rendered
        // the adult app optimistically (see dancerSessionHint.ts) must not
        // have it yanked into the full-page AccessCheckError by a
        // transient failure of the SAME background check - the earlier
        // confirmed "no dancer cookie" evidence is still this device's
        // best information. Falls back to 'signed-out', the state the
        // last successful check already put it in; the existing retry
        // path (retryKey, or simply a fresh reload) still re-runs this
        // check later. A non-optimistic load keeps the modal error,
        // unchanged from before this fix.
        const failureState = statusCheckFailureState({
          noDancerHint,
          hasFirebaseUser: Boolean(firebaseUser),
        });
        if (failureState === 'error') setError(messageFor(caught));
        setState(failureState);
        // NO shell clear here, deliberately (Codex review of PR #1099, P1).
        // This is a transient failure of the check, not an answer from it,
        // and the whole point of the branch above is that a 5xx or an
        // offline phone is not new evidence against what this device
        // already knows. The next successful check clears the shell if the
        // session really is gone.
      }
    })();

    return () => {
      current = false;
    };
  }, [firebaseLoading, firebaseUser, hadDancerShell, noDancerHint, retryKey]);

  const login = useCallback(async (username: string, password: string) => {
    if (firebaseUser) {
      throw new DancerSessionError('Sign out of the adult account before using a dancer login.');
    }
    setError(null);
    setSessionNotice(null);
    // A new attempt starts the story over: whatever an earlier login left in
    // memory must not be read as evidence about this one.
    justSignedInRef.current = null;
    const generation = ++loginGenerationRef.current;
    try {
      const status = await loginDancerSession(username, password);
      setCsrfToken(status.csrfToken);
      setState('signed-in');
      cookieExpectedRef.current = true;
      // Remembered only once the server has said yes. If the next status
      // answer finds no cookie, this is how the card knows the sign-in
      // worked and the BROWSER did not keep it.
      justSignedInRef.current = rememberJustSignedIn(Date.now());
      // A dancer cookie now definitely exists on this device: any stale
      // "no dancer" hint left over from an earlier adult session here would
      // otherwise let a FUTURE adult load skip the wait on stale evidence.
      clearNoDancerHint();
      // Same fire-and-forget parity exchange as the status effect above: a
      // promoted Jr Staff gains their staff session without their own
      // sign-in ever waiting on it. But the token in the LOGIN response is
      // NOT redeemed: this classifies first. If the cookie was never kept
      // (the home-screen app), minting a Firebase principal now would send
      // her down the promoted dead-cookie branch, whose full sign-out reloads
      // the page and takes the in-memory evidence with it, so a promoted Jr
      // Staff would read a different notice than any other dancer (DECISIONS
      // 26 and 27: one person, however she signs in). So ask the server once
      // whether the cookie came back, and exchange the token that answer
      // carries only when it did. A cookie that never came back mints no
      // principal, signs nothing out, and reloads nothing.
      if (status.jrStaffToken) {
        setPromotionPending(true);
        void (async () => {
          let started = false;
          try {
            // A NETWORK failure (a timeout, a 503, a dropped fetch) is not an
            // answer: it says nothing about the cookie. Try once more before
            // giving up, so a blip does not cost a promoted Jr Staff her staff
            // session for this load. A clear answer, authenticated or not,
            // is taken as it comes.
            let confirmed = await getDancerSessionStatus().catch(() => null);
            if (!confirmed) {
              await new Promise((resolve) => setTimeout(resolve, CONFIRM_RETRY_DELAY_MS));
              confirmed = await getDancerSessionStatus().catch(() => null);
            }
            if (
              loginGenerationRef.current === generation
              && confirmed?.authenticated
              && confirmed.jrStaffToken
            ) {
              started = await signInAsJrStaffDancer(confirmed.jrStaffToken);
            }
          } catch {
            started = false;
          }
          if (!started) setPromotionPending(false);
        })();
      }
    } catch (caught) {
      setCsrfToken(null);
      setError(messageFor(caught));
      setState('signed-out');
      // NO shell clear here either, deliberately (Codex review of PR #1099,
      // P1). A failed LOGIN says this attempt did not create a session; it
      // says nothing about whether a dancer cookie already lives on this
      // device, and the status check above is the thing that answers that,
      // on this load and on every load after it. Clearing here could only
      // throw away a correct shell belonging to a dancer who is signed in
      // and mistyped a second login.
      throw caught;
    }
  }, [firebaseUser]);

  // SIGN OUT CLEARS BOTH SESSIONS. A promoted Jr Staff dancer holds a
  // dancer cookie AND a real Firebase principal, and her users doc is
  // {ensemble, active} - indistinguishable from an adult Jr Staff account,
  // which is the parity ruling working as intended. Clearing only the
  // cookie therefore did not sign her out at all: App.tsx stopped routing
  // the tab to the dancer portal and mounted the FULL STAFF APP as her for
  // the next person on the device. The decision and the ordering live in
  // lib/dancerSessionLogout.ts, where a test can execute them.
  const logout = useCallback(async () => {
    const token = csrfToken;
    setError(null);
    setSessionNotice(null);
    // A deliberate sign-out ends the story; nothing said after it is about a
    // cookie that was never kept.
    justSignedInRef.current = null;
    // She is ending it on purpose: nothing is left to re-ask for (B15).
    cookieExpectedRef.current = false;
    // Any confirming status call still in flight is about a session she is
    // ending now; see loginGenerationRef.
    loginGenerationRef.current += 1;
    // PACKET J12 ITEM 3: same shared-device reasoning as the adult sign-out
    // in lib/auth.ts. The next person to open the Hub on this phone must
    // not see this dancer's first name on the boot screen.
    //
    // BEFORE the teardown, not after (Codex review of PR #1099, P1, while
    // auditing every path that ends signed out). runDancerLogout can throw
    // part way through, with the cookie already cleared and only the
    // Firebase teardown failed, and the catch below then skips everything
    // after the await. Forgetting a name too early costs one boot of the
    // plain spinner and the next resolved boot writes it again; forgetting
    // it too late leaves a child's name on a phone that just signed out.
    clearLastHomeShell();
    // Same shared-device reasoning for the Sentry dancer tag: the next
    // person on this phone is not this kid.
    clearSentryDancerSession();
    // And her last costume pickup answers, for the same reason.
    forgetDancerCostumeStates();
    try {
      // Her Big/Little phone alerts come off this phone while her session
      // is still live, and this browser forgets the device and its preview
      // identity (lib/dancerPushDevice.ts). Never throws, and waits briefly.
      await releaseDancerPushDeviceOnSignOut({
        csrfToken: token,
        remove: removeDancerPushDevice,
        clearPreview: clearPushPreviewIdentity,
      });
      await runDancerLogout({
        csrfToken: token,
        hasFirebaseSession: Boolean(firebaseUser),
        clearDancerCookie: logoutDancerSession,
        signOutFirebase: signOut,
      });
      setCsrfToken(null);
      setState('signed-out');
      // The cookie is confirmed cleared: mark it so a NEXT adult session on
      // this device does not wait on a fetch that would only re-confirm
      // what logout just did directly. (For a promoted Jr Staff, signOut
      // above ends in a page reload, so this line never runs here - the
      // reload's own fresh status check marks it instead, just as safely.)
      markNoDancerHint();
    } catch (caught) {
      setError(messageFor(caught));
      // Only a live cookie can be left signed in. With no token there was
      // never a dancer session to restore, so the honest resting state is
      // signed out even when the Firebase teardown was what failed.
      setState(token ? 'signed-in' : 'signed-out');
      if (!token) setCsrfToken(null);
      throw caught;
    }
  }, [csrfToken, firebaseUser]);

  const retry = useCallback(() => setRetryKey((key) => key + 1), []);
  const updateCsrfToken = useCallback((nextToken: string) => {
    if (!nextToken) return;
    // The only caller is the portal answering 200 with a fresh token, and
    // that call needs the cookie: the cookie WAS kept. Whatever is lost later
    // (a sign-out in another tab, a wrong password typed in one, an adult
    // signing in) is not the app failing to keep this login.
    justSignedInRef.current = null;
    setCsrfToken(nextToken);
  }, []);

  const value = useMemo<DancerSessionContextValue>(() => ({
    state,
    authenticated: state === 'signed-in',
    // Unchanged and deliberately boolean: null reads as false here, exactly
    // as the old `false` initial state did, so no route or chrome decision
    // moves with this change.
    jrStaffDancer: state === 'signed-in' && jrStaffDancer === true,
    jrStaffPromotion: jrStaffPromotionState({
      signedIn: state === 'signed-in',
      firebaseLoading,
      promotionPending,
      claim: jrStaffDancer,
    }),
    noDancerHint,
    csrfToken,
    error,
    sessionNotice,
    login,
    logout,
    retry,
    updateCsrfToken,
  }), [state, jrStaffDancer, firebaseLoading, promotionPending, noDancerHint, csrfToken, error, sessionNotice,
    login, logout, retry, updateCsrfToken]);

  return <DancerSessionContext.Provider value={value}>{children}</DancerSessionContext.Provider>;
}

/**
 * The dancer session when there is one, else null instead of throwing. For
 * code that also mounts on the public pages (/about, /privacy, /terms), where
 * App renders the routes with no DancerSessionProvider (lib/routeWarm.ts).
 */
export function useOptionalDancerSession(): DancerSessionContextValue | null {
  return useContext(DancerSessionContext);
}

export function useDancerSession(): DancerSessionContextValue {
  const value = useContext(DancerSessionContext);
  if (!value) throw new Error('useDancerSession must be used inside DancerSessionProvider.');
  return value;
}
